import { useMutation } from "@apollo/client";
import { ReactNode, useState } from "react";

import { factorial } from "../../utils";
import { Game, QuickRankConfiguration, QuickRankMode, ResponseKind } from "../../gql/graphql";
import { QuickRankDestinationsProvider } from "./destination";
import { QuickRankGameResults } from "../../reducers/quickrank";
import { QuickRankStateProvider } from "./state";
import { RESPONSE_CREATE } from "../../graphql/mutations/Response";
import { useGlobalConfiguration } from "../../hooks/useGlobalConfiugration";
import { integrationApi } from "../../main";
import { NicknameModal } from "../../components/shared/NicknameModal";
import { isEmpty } from "lodash";

interface QuickRankContextProviderProps {
  children: ReactNode;
  game: Game;
  onGameFinished: (newResponseId?: string) => void;
}

export const QuickRankContextProvider = ({ game, children, onGameFinished }: QuickRankContextProviderProps) => {
  const [nickname, setNickname] = useState<string | undefined>(undefined);
  const { attributes, mode, showLeaderboard, leaderboardConfiguration } = game.configuration as QuickRankConfiguration;
  const [responseCreate] = useMutation(RESPONSE_CREATE);
  const { isPreviewMode } = useGlobalConfiguration();
  const { projectNickname } = useGlobalConfiguration();
  const isLeaderboardVisible = showLeaderboard && !nickname && !projectNickname;

  const { cardsInDeck, cardsPerHand } = {
    [QuickRankMode.TwoPickN]: {
      cardsInDeck: (factorial(attributes.length) / (2 * factorial(attributes.length - 2))) * 2,
      cardsPerHand: 2,
    },
    [QuickRankMode.AllUp]: {
      cardsInDeck: attributes.length,
      cardsPerHand: attributes.length,
    },
    [QuickRankMode.Optimized]: {
      cardsInDeck: 100,
      cardsPerHand: 2,
    },
  }[mode];

  const configuration = {
    ...(game.configuration as QuickRankConfiguration),
    cardsPerHand,
    numberOfHands: Math.floor(cardsInDeck / cardsPerHand),
  };

  const handleGameFinish = async (results: QuickRankGameResults, startTime: Date) => {
    if (isPreviewMode) {
      return onGameFinished("");
    }

    const responseKind =
      integrationApi.configuration.provider === "alchemer" ? ResponseKind.Alchemer : ResponseKind.Hosted;

    const { data } = await responseCreate({
      variables: {
        data: {
          responseKind,
          startTime: startTime.toISOString(),
          endTime: new Date().toISOString(),
          nickname: mode === QuickRankMode.AllUp ? projectNickname || nickname : null,
          gameId: game.id,
          quickRankScores: Object.keys(results).map((attributeId) => ({
            attributeId,
            ...results[attributeId],
          })),
        },
      },
    });

    onGameFinished(String(data?.responseCreate.slug) ?? "");
  };

  const showGame = isLeaderboardVisible ? !isEmpty(nickname) : true;

  return (
    <QuickRankDestinationsProvider cardsInDeck={cardsInDeck} cardsPerHand={cardsPerHand}>
      <NicknameModal
        isOpen={isLeaderboardVisible}
        gameId={game.id}
        themeColor={game.configuration.gameVisuals.themeColor}
        onContinue={setNickname}
        configuration={leaderboardConfiguration}
      />

      {showGame && (
        <QuickRankStateProvider
          configuration={configuration}
          cardsPerHand={cardsPerHand}
          onGameFinished={handleGameFinish}
        >
          {children}
        </QuickRankStateProvider>
      )}
    </QuickRankDestinationsProvider>
  );
};
